import React, { FC, useEffect, useState } from 'react';
import { Box, TextField, Button, Typography, Grid, MenuItem, CircularProgress, useMediaQuery } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useTheme } from '@mui/material/styles';
import ModalActionBar from 'components/modal-action-bar';
import { useTranslation } from 'react-i18next';
import { AvailableModel } from 'views/ai-config/types';

const useStyles = makeStyles((theme) => ({
  formContainer: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(1.5),
    },
  },
  formContent: {
    flex: 1,
    overflowY: 'auto',
    paddingRight: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  actionBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
  },
  actionButton: {
    [theme.breakpoints.down('md')]: {
      flex: 1,
    },
  },
}));

interface AiConfigFormProps {
  aiOptions: AiOptionsWithoutPrompts | null;
  availableModels: AvailableModel[] | null;
  onCancel: () => void;
  onSubmit: (data: AiUpdateOptionsFormData) => Promise<void>;
  isSubmitting: boolean;
}

interface AiOptionsWithoutPrompts {
  model_agent: string;
  model_vision: string;
  prompt_system: {
    english: string;
  };
  prompt_screen: {
    english: string;
  };
  prompt_image: {
    english: string;
  };
}

interface AiUpdateOptionsFormData {
  model_agent: string;
  model_vision: string;
  prompt_system: {
    english: string;
  };
  prompt_screen: {
    english: string;
  };
  prompt_image: {
    english: string;
  };
}

export const AiConfigForm: FC<AiConfigFormProps> = ({ aiOptions, availableModels, onCancel, onSubmit, isSubmitting }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [formData, setFormData] = useState<AiUpdateOptionsFormData>({
    model_agent: aiOptions?.model_agent || '',
    model_vision: aiOptions?.model_vision || '',
    prompt_system: { english: aiOptions?.prompt_system?.english || '' },
    prompt_screen: { english: aiOptions?.prompt_screen?.english || '' },
    prompt_image: { english: aiOptions?.prompt_image?.english || '' },
  });

  const [previousOptions, setPreviousOptions] = useState(aiOptions);
  if (previousOptions !== aiOptions) {
    setPreviousOptions(aiOptions);
    if (aiOptions) {
      setFormData({
        model_agent: aiOptions.model_agent || '',
        model_vision: aiOptions.model_vision || '',
        prompt_system: {
          english: aiOptions.prompt_system?.english || '',
        },
        prompt_screen: {
          english: aiOptions.prompt_screen?.english || '',
        },
        prompt_image: {
          english: aiOptions.prompt_image?.english || '',
        },
      });
    }
  }

  const isValid =
    formData.model_agent.trim() !== '' &&
    formData.prompt_system.english.trim() !== '';

  const handleSubmit = async () => {
    await onSubmit(formData);
  };

  const handleChange = (field: string, value: string, subField?: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: subField
        ? { ...prev[field], [subField]: value }
        : value,
    }));
  };

  const modelOptions = availableModels?.map((model) => ({
    value: model.id,
    label: model.id,
  }));

  return (
    <Box className={classes.formContainer}>
      <Box className={classes.formContent}>
        <Typography variant="h5" gutterBottom>
          {t('aiConfig:configureAI')}
        </Typography>

        <Grid container spacing={isMobile ? 1 : 2}>
          {/* Model selection fields */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              label={t('aiConfig:modelAgent')}
              value={formData.model_agent}
              onChange={(e) => handleChange('model_agent', e.target.value)}
              margin="normal"
              required
              disabled={isSubmitting}
            >
              {modelOptions?.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* System Prompt */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>{t('aiConfig:promptSystem')}</Typography>
            <TextField
              fullWidth
              label={t('aiConfig:english')}
              value={formData.prompt_system.english}
              onChange={(e) => handleChange('prompt_system', e.target.value, 'english')}
              multiline
              {...(isMobile ? { minRows: 8, maxRows: 12 } : { minRows: 20, maxRows: 30 })}
              margin="normal"
              required
              disabled={isSubmitting}
              InputProps={{ style: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
            />
          </Grid>
        </Grid>
      </Box>

      <ModalActionBar
        onCancel={onCancel}
        submitType="button"
        submitText={t('aiConfig:save')}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        submitDisabled={!isValid}
      />
    </Box>
  );
};
